import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { useTranslation } from "react-i18next";
import { Tabs } from "antd";
import { Banner } from "@companion-core/shared/app/Components/Banner";
import { RequestList } from "@companion-core/shared/app/Components/Requests";
import { APIRequest } from "@companion-core/shared/app/Interfaces/API";
import { useRequests } from "@companion-core/shared/app/Queries";
import { getModule } from "@companion-core/shared/app/Utils/config";
import { slugifyRequest } from "@companion-core/shared/app/Utils/myRequests";
import { trackPageRequests } from "@companion-core/shared/app/tracking/requests";
import { useMediaQuery } from "@companion-core/web/src/Utilities/mediaQuery";
import { useTabsAutoActivate } from "@companion-core/web/src/Hooks/useTabsAutoActivate";
import "Assets/Styles/scss/myRequests.scss";

const Requests = () => {
  const headerImgURL = getModule("concierge").modules["my-requests"].assets.headerImg;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { route } = useParams();
  const { isPending, data } = useRequests();
  const isMobileSize = useMediaQuery("(max-width: 768px)");
  const [activeKey, setActiveKey] = useState<number>(route === "past" ? 1 : 0);
  const requests = useMemo(
    () =>
      (data?.data?.results as APIRequest[])?.map((item) => slugifyRequest(item) as APIRequest) ??
      [],
    [data]
  ); // TODO: [SLUGIFY_REQUEST] simplify like: const requests = useMemo(() => (data?.data?.results as APIRequest[]) ?? [], [data]);

  const onGoingRequests = useMemo(
    () => requests.filter((item) => item.status !== "closed" && item.status !== "cancelled"),
    [requests]
  );
  const doneRequests = useMemo(
    () => requests.filter((item) => item.status === "closed" || item.status === "cancelled"),
    [requests]
  );

  const renderTabLabel = (label: string, count: number, key: string) => (
    <span>
      <span aria-label={t(`common.my-requests.tab.list-info.${key}`)}>{label}</span>
      {count > 0 && (
        <span className="my-requests__sr-only">
          {t(
            count > 1
              ? "common.my-requests.tab.list-info.plural"
              : "common.my-requests.tab.list-info.singular",
            {
              count,
            }
          )}
        </span>
      )}
    </span>
  );

  // display page tracking
  useEffect(() => {
    trackPageRequests({ state: activeKey == 0 ? "InProgress" : "Closed" });
  }, [activeKey]);

  const navigateTabsRoute: Record<string, () => void> = {
    "0": () => navigate("/my-requests/past"),
    "1": () => navigate("/my-requests"),
  };

  const handleTabChange = (key: string) => {
    setActiveKey(parseInt(key));
    navigateTabsRoute[activeKey]();
  };

  const tabsContainerRef = useTabsAutoActivate<HTMLDivElement>(
    handleTabChange,
    activeKey.toString()
  );

  return (
    <div className="my-requests" ref={tabsContainerRef}>
      {!isMobileSize ? (
        <Banner
          title={t("common.my-requests.title.label")}
          imageUrl={headerImgURL}
          disappearOnMobileSize
          boxShadow={true}
          titleTag="h1"
        />
      ) : (
        <h1 className="my-requests__title title-6">{t("common.my-requests.title.label")}</h1>
      )}
      <Tabs
        id="my-requests-tabs"
        className="my-requests__tab-content"
        activeKey={activeKey.toString()}
        onChange={(key) => handleTabChange(key)}
        centered
        lang="fr"
        items={[
          {
            key: "0",
            label: renderTabLabel(
              t("common.my-requests.tab.on-going.label"),
              onGoingRequests.length,
              "on-going"
            ),
            children: (
              <RequestList
                activeKey={activeKey}
                requestsFetched={!isPending}
                data={onGoingRequests}
              />
            ),
          },
          {
            key: "1",
            label: renderTabLabel(
              t("common.my-requests.tab.done.label"),
              doneRequests.length,
              "done"
            ),
            children: (
              <RequestList activeKey={activeKey} requestsFetched={!isPending} data={doneRequests} />
            ),
          },
        ]}
      />
    </div>
  );
};

export default Requests;
